import React, {
  useReducer,
  useImperativeHandle,
  useEffect,
  useRef,
  useMemo,
  // useState,
} from 'react';
import classnames from 'classnames';

import Checkbox from 'components/elements/atoms/Checkbox/Checkbox';
import QuestionText from './QuestionText';
import SelectPointsQuestion from './SelectPointsQuestion';
import MultipleChoicePointQuestion from './MultipleChoicePointQuestion';
import MultipleSelectPointsQuestion from './MultipleSelectPointsQuestion';
import InputTextQuestion from './InputTextQuestion';
import InputTextAreaQuestion from './InputTextAreaQuestion';
import InputQuestions from './InputQuestions';
import SingleChoiceButton from './SingleChoiceButton';
import CounterGroup from './CounterGroup';
import PreFillSources from './PreFillSources';
import SourcePickerChoice from './SourcePickerChoice';
import LocationPicker from './LocationPicker';
import SchoolPickerQuestion from './SchoolPickerQuestion';
import InfoQuestion from './InfoQuestion';
import EmailCheckInputQuestion from './EmailCheckInputQuestion';
import ActionLink from '../ActionLink/ActionLink';

import './Survey.scss';

function Survey(
  {
    questions,
    // totalSteps,
    onFinish,
    className,
    onChangeIndex,
    onChangeModel,
    point,
    points,
    oldModel,
    onAction,
    startIndex = 0,
    isMock,
    collectionPoint,
    firstName,
    lastName,
    isExtensionCL,
    mode = 'SINGLE', // SINGLE, ALL
    model,
    shouldDisabled,
  },
  ref,
) {
  const index = useMemo(() => {
    const firstQuestion = questions[0];

    if (firstQuestion.goToIfNameExists && firstName && lastName) {
      return firstQuestion.goToIfNameExists;
    }

    return startIndex;
  }, [questions]);

  const [state, dispatch] = useReducer(
    (state, action) => {
      switch (action.type) {
        case 'SET_INDEX':
          return {
            ...state,
            index: action.payload,
            submitted: false,
          };
        case 'SET_VARIABLE':
          return {
            ...state,
            model: {
              ...state.model,
              [action.payload.variable]: action.payload.value,
            },
          };
        case 'SET_SUBMITTED':
          return { ...state, submitted: action.payload };
        case 'SET_PREVIOUS_INDEX':
          return {
            ...state,
            previousIndexes: action.payload,
          };
        case 'SET_MODEL':
          return {
            ...state,
            model: action.payload,
          };
        default:
          return state;
      }
    },
    { index, model: { ...model }, submitted: false, previousIndexes: [] },
  );

  const questionRef = useRef([]);

  useImperativeHandle(ref, () => ({
    totalQuestions: () => {
      return questions.length;
    },
    getActiveIndex: () => {
      return getActiveIndex();
    },
    getActiveQuestion: () => {
      return getActiveQuestion();
    },
    allowContinue: () => {
      return allowContinue(state.index);
    },
    allowPrevious: () => {
      return state.previousIndexes.length;
    },
    previous: () => {
      onPrevious();
    },
    continue: () => {
      onContinue();
    },
    allowSave: () => {
      // let allow = true;

      // questions.forEach((question, index) => {
      //   if (checkVisibleCondition(question)) {
      //     allow = allow && allowContinue(index);
      //   }
      // });

      // return allow;
      return allowSave(questions, '');
    },
    setModel(model) {
      dispatch({ type: 'SET_MODEL', payload: model });
    },
  }));

  const allowSave = (questions, parentIndex) => {
    let allow = true;

    questions.forEach((question, index) => {
      if (checkVisibleCondition(question)) {
        allow = allow && allowContinue(+`${parentIndex}${index}`, question);

        if (question.questions && question.questions.length) {
          allow =
            allow && allowSave(question.questions, `${parentIndex}${index}`);
        }
      }
    });

    return allow;
  };

  useEffect(() => {
    if (onChangeIndex) {
      onChangeIndex(state.index);
    }

    // set old model value
    if (oldModel) {
      const question = questions[state.index];
      const variable = question.variable;

      if (variable) {
        onChange(oldModel[variable]);
      }
    }
  }, [state.index]);

  useEffect(() => {
    if (onChangeModel) {
      onChangeModel(state.model);
    }
  }, [state.model]);

  function onContinue() {
    dispatch({ type: 'SET_SUBMITTED', payload: true });

    if (!allowContinue(state.index)) {
      return;
    }

    if (
      questionRef.current &&
      questionRef.current[state.index] &&
      questionRef.current[state.index].continue
    ) {
      const ret = questionRef.current[state.index].continue();

      if (ret) {
        return;
      }
    }

    next();
  }

  function allowContinue(index, question) {
    if (
      questionRef.current[index] &&
      questionRef.current[index].allowContinue
    ) {
      return questionRef.current[index].allowContinue();
    } else {
      question = question ?? questions[index];
      return (
        !question.required ||
        !!(question.variable && state.model[question.variable])
      );
    }
  }

  function getActiveIndex() {
    return state.index;
  }

  function getActiveQuestion() {
    return questions[getActiveIndex()];
  }

  const checkVisibleCondition = (question) => {
    if (question.visibleCondition) {
      const visibleConditionKeys = Object.keys(question.visibleCondition);
      if (
        visibleConditionKeys.filter((i) => {
          if (question.visibleCondition[i] === 'value!undefined') {
            return !state.model[i];
          } else {
            return state.model[i] === question.visibleCondition[i];
          }
        }).length !== visibleConditionKeys.length
      ) {
        return false;
      }
    }

    return true;
  };

  function setIndex(index, push) {
    if (push) {
      dispatch({
        type: 'SET_PREVIOUS_INDEX',
        payload: [...state.previousIndexes, state.index],
      });
    } else {
      dispatch({
        type: 'SET_PREVIOUS_INDEX',
        payload: state.previousIndexes.slice(
          0,
          state.previousIndexes.length - 1,
        ),
      });
    }
    dispatch({ type: 'SET_INDEX', payload: index });
  }

  function next() {
    const question = getActiveQuestion();
    question.type === 'END' ? onFinishQuestions() : onNext();
  }

  function onChange(value, variable, question) {
    if (!variable) {
      question = question || questions[state.index];
      variable = question.variable;
    }

    if (variable) {
      dispatch({
        type: 'SET_VARIABLE',
        payload: { variable, value },
      });
    }
  }

  function onNext() {
    const question = questions[state.index];
    const questionModel = state.model[question.variable];
    let goto;

    if (
      question.type === 'SINGLE_CHOICE' ||
      question.type === 'SINGLE_CHOICE_BUTTON'
    ) {
      goto = question.options.find((i) => i.text === questionModel).goto;
    } else if (
      question.type === 'MULTIPLE_CHOICE' &&
      (!questionModel || !questionModel.length) &&
      question.noSelectGoto !== undefined
    ) {
      goto =
        question.noSelectGoto === 'END' ? undefined : question.noSelectGoto;
    } else {
      goto = question.goto;
    }
    if (goto && questions[goto] && questions[goto].goToIfNameExists) {
      if (firstName && lastName) {
        goto = questions[goto].goToIfNameExists;
      }
    }

    if (!goto && question.finalQuestion) {
      onFinishQuestions();
    } else {
      setIndex(goto, true);
      dispatch({ type: 'SET_VALUE', payload: undefined });
    }
  }

  function onPrevious() {
    if (
      questionRef.current &&
      questionRef.current[state.index] &&
      questionRef.current[state.index].previous
    ) {
      const ret = questionRef.current[state.index].previous();

      if (ret) {
        return;
      }
    }

    onChange(undefined);
    setIndex(state.previousIndexes[state.previousIndexes.length - 1], false);
  }

  function onFinishQuestions() {
    onFinish(state.model);
  }

  function renderQuestion(question, index) {
    // const question = questions[state.index];

    if (!checkVisibleCondition(question)) {
      return null;
    }

    let Component;

    switch (question.type) {
      case 'TEXT':
      case 'END':
        Component = TextQuestion;
        break;
      case 'SINGLE_CHOICE':
        Component = SingleChoiceQuestion;
        break;
      case 'MULTIPLE_CHOICE':
        Component = MultipleChoiceQuestion;
        break;
      case 'PRE_FILL_SOURCES':
        Component = PreFillSources;
        break;
      case 'INPUT_TEXT':
        Component = InputTextQuestion;
        break;
      case 'INPUT_TEXT_AREA':
        Component = InputTextAreaQuestion;
        break;
      case 'INPUT_QUESTIONS':
        Component = InputQuestions;
        break;
      case 'SELECT_POINTS':
        Component = SelectPointsQuestion;
        break;
      case 'MULTIPLE_CHOICE_POINT':
        Component = MultipleChoicePointQuestion;
        break;
      case 'MULTIPLE_SELECT_POINTS':
        Component = MultipleSelectPointsQuestion;
        break;
      case 'SINGLE_CHOICE_BUTTON':
        Component = SingleChoiceButton;
        break;
      case 'INFO':
        Component = InfoQuestion;
        break;
      case 'COUNTER_GROUP':
        Component = CounterGroup;
        break;
      case 'SOURCE_PICKER':
        Component = SourcePickerChoice;
        break;
      case 'LOCATION_PICKER':
        Component = LocationPicker;
        break;
      case 'SCHOOL_PICKER':
        Component = SchoolPickerQuestion;
        break;
      case 'EMAIL_CHECK_INPUT_TEXT':
        Component = EmailCheckInputQuestion;
        break;
      case 'GROUP_QUESTIONS': {
        const containerClassName = classnames('group-questions question', {
          disabled: shouldDisabled ? shouldDisabled(question) : false,
        });
        return (
          <div className={containerClassName}>
            {question.questions.map((question, subIndex) =>
              renderQuestion(question, +`${index}${subIndex}`),
            )}
          </div>
        );
      }
      // ADDITIVE, AND NOTHING ELSE USES IT. A line shown under a question the
      // moment it is answered, for the one section that teaches on a wrong
      // answer (classroom surveys, 'Understanding the assignment'). The
      // engine renders each question bare, so a caller cannot put a
      // distinguishable line under one - hence a type rather than a hack with
      // an empty question whose subText carries the copy.
      case 'FEEDBACK': {
        const feedbackClassName = classnames('survey-feedback', {
          correct: question.correct,
        });
        return <div className={feedbackClassName}>{question.text}</div>;
      }
      case 'TOGGLE_LINK': {
        const containerClassName = classnames('toggle-link-question question', {
          disabled: shouldDisabled ? shouldDisabled(question) : false,
        });
        return (
          <div className={containerClassName}>
            <ActionLink
              linkClassName="av-blue"
              iconType="averPointIcon"
              iconName={
                state.model[question.variable]
                  ? question.onIconName
                  : question.offIconName
              }
              label={
                state.model[question.variable]
                  ? question.onText
                  : question.offText
              }
              onClick={() => {
                onChange(
                  state.model[question.variable] ? undefined : question.offText,
                  question.variable,
                  question,
                );
              }}
              disabled={shouldDisabled ? shouldDisabled(question) : false}
            ></ActionLink>
          </div>
        );
      }

      // AN UNKNOWN TYPE MUST NOT TAKE THE PAGE DOWN, AND MUST NOT GO QUIET.
      //
      // Without this, Component stays undefined and falls through to
      // <Component> below - React throws on an undefined element type and,
      // there being no boundary here, unmounts the whole tree. That is how
      // survey 3 became untakeable: one unhandled type blanked the section it
      // was in and every section after it.
      //
      // Rendering nothing silently is the opposite failure and no better. The
      // question would disappear with nobody told, which is exactly how a
      // defect sits unnoticed for months. So the question is skipped, the
      // survey survives, and the console says which type and which question -
      // by name, because "unknown type" alone does not tell you where to look.
      default: {
        const label = question.variable || question.name || 'unnamed';

        console.error(
          `Survey: no component for question type "${question.type}" ` +
            `(question "${label}"). The question was SKIPPED - add it to the ` +
            `switch in Survey.js.`,
        );

        // Visible while developing, gone from the production bundle: CRA
        // inlines NODE_ENV, so this block is eliminated at build time.
        if (process.env.NODE_ENV !== 'production') {
          return (
            <div className="question survey-unknown-type">
              Unknown question type <code>{question.type}</code> for{' '}
              <code>{label}</code> — skipped. This message is not shown in
              production.
            </div>
          );
        }

        return null;
      }
    }

    return (
      <Component
        questions={questions}
        question={question}
        onChange={(value, variable) => onChange(value, variable, question)}
        model={state.model}
        submitted={state.submitted}
        point={point}
        points={points}
        ref={(elm) => {
          questionRef.current[index] = elm;
        }}
        onContinue={onContinue}
        onAction={onAction}
        isMock={isMock}
        collectionPoint={collectionPoint}
        firstName={firstName}
        lastName={lastName}
        isExtensionCL={isExtensionCL}
        key={index}
        shouldDisabled={shouldDisabled}
      />
    );
  }

  const renderQuestions = () => {
    return questions.map(renderQuestion);
  };

  const containerClass = classnames('survey-component', className, {
    'all-mode': mode === 'ALL',
  });

  return (
    <div className={containerClass} ref={ref}>
      {mode === 'ALL'
        ? renderQuestions()
        : renderQuestion(questions[state.index], state.index)}
    </div>
  );
}

export default React.forwardRef(Survey);

function TextQuestion({ question }) {
  function renderText() {
    return <QuestionText question={question} />;
  }
  return <>{renderText()}</>;
}

export function SingleChoiceQuestion({
  question,
  onChange,
  model,
  shouldDisabled,
}) {
  // const [focused, setFocused] = useState(false);

  function renderText() {
    return <QuestionText question={question} />;
  }

  function renderCheckbox(option, key) {
    const value = model[question.variable];

    // if (option && option.text === value && option.showNote) {
    //   return null;
    // }

    return (
      <Checkbox
        onClick={(e) => {
          e.preventDefault();
          onChange(option.text === value ? undefined : option.text);
        }}
        onChange={() => {}}
        checked={value === option.text}
        key={key}
        className="mt-2"
        disabled={shouldDisabled ? shouldDisabled(question) : false}
      >
        {option.text}
      </Checkbox>
    );
  }

  function renderCheckboxes() {
    return question.options.map((option, index) =>
      renderCheckbox(option, index),
    );
  }

  function renderNote() {
    const value = model[question.variable];
    const option = question.options.find((option) => option.text === value);

    if (!option || !option.showNote) {
      return null;
    }

    const noteValue = model[option.showNoteVariable];

    return (
      <textarea
        className="form-control mt-2"
        placeholder={option.showNotePlaceholder}
        value={noteValue}
        onChange={(e) => onChange(e.target.value, option.showNoteVariable)}
        style={{ marginLeft: '20px' }}
      />
    );
  }

  const containerClassNames = classnames('question single-choice-question', {
    // focused: focused,
    disabled: shouldDisabled ? shouldDisabled(question) : false,
  });
  return (
    <div
      // onFocus={() => setFocused(true)}
      // onBlur={() => setFocused(false)}
      // tabIndex={1}
      className={containerClassNames}
    >
      {renderText()}
      {renderCheckboxes()}
      {renderNote()}
    </div>
  );
}

function MultipleChoiceQuestion({ question, onChange, model, shouldDisabled }) {
  // const [focused, setFocused] = useState(false);

  function getValue() {
    return model[question.variable] || [];
  }

  function isChecked(option) {
    if (option.clear) {
      return getValue().length === 0;
    } else {
      return getValue().indexOf(option.text) !== -1;
    }
  }

  function renderText() {
    return <QuestionText question={question} />;
  }

  function renderCheckbox(option, key) {
    const value = getValue();
    const checked = isChecked(option);
    // "Choose up to 3" has to mean it. Only questions carrying maxSelections
    // are capped, so nothing else changes: once the cap is reached the
    // unchecked options go disabled until one is cleared.
    const atCap =
      !!question.maxSelections &&
      !checked &&
      !option.clear &&
      value.length >= question.maxSelections;

    return (
      <Checkbox
        disabled={atCap}
        className={classnames('mt-2', { 'at-cap': atCap })}
        onClick={(e) => {
          e.preventDefault();
          if (atCap) return;

          if (checked) {
            value.splice(value.indexOf(option.text), 1);
            onChange([...value]);
          } else {
            if (option.clear) {
              onChange([]);
            } else {
              onChange([...value, option.text]);
            }
          }
        }}
        onChange={(e) => {}}
        checked={checked}
        key={key}
      >
        {option.text}
      </Checkbox>
    );
  }

  function renderCheckboxes() {
    return question.options.map((option, index) =>
      renderCheckbox(option, index),
    );
  }

  function renderNote() {
    // const value = getValue();

    const option = question.options.find(
      (option) => isChecked(option) && option.showNote,
    );

    if (!option) {
      return null;
    }

    const noteValue = model[option.showNoteVariable];

    return (
      <textarea
        className="form-control mt-2"
        placeholder={option.showNotePlaceholder}
        value={noteValue}
        onChange={(e) => onChange(e.target.value, option.showNoteVariable)}
        style={{ marginLeft: '20px' }}
      />
    );
  }

  const containerClassNames = classnames('question multiple-choice-question', {
    // focused: focused,
    disabled: shouldDisabled ? shouldDisabled(question) : false,
  });

  return (
    <div
      // onFocus={() => setFocused(true)}
      // onBlur={() => setFocused(false)}
      // tabIndex={1}
      className={containerClassNames}
    >
      {renderText()}
      {renderCheckboxes()}
      {renderNote()}
    </div>
  );
}
